import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import { usePersona } from "../../context/usePersona";
import { getStages } from "../../shared/British";
import { Stage } from "../../shared/CaseConfiguration";
import { judgmentOf } from "../../shared/judgment";
import {
  IsoDate,
  isMonths,
  StageOnTimeline,
  StageStatus,
  stagesOnTimeline,
  Timeline,
  todayIso,
  endDateRange,
  isPassedOver,
  settledStageOf,
  showDate,
  withEnded,
  withMonths,
} from "../../shared/timeline";
import { SectionLabel } from "../general/SectionLabel";
import { TonalBadge } from "../general/TonalBadge";
import { GuideHeader } from "../guide/GuideHeader";
import { Marked } from "../guide/GuideMarker";
import { useStageClosing } from "./useStageClosing";

const card = { borderRadius: 3 } as const;

/** A date as the pages show it: 21 Jun 2026. */
export { showDate };

type DateField = "start" | "lossDate" | "notifiedDate";

/**
 * When the case started and how long the user expects each stage to take, and
 * where the stages stand: closed, open, overdue, planned or not planned. Only
 * what the user enters is planned.
 */
export const TimelinePage = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const { t: tRoot } = useTranslation();
  const caseContext = useCase();
  const { readOnly } = caseContext;
  const c = caseContext.getCase();
  const submit = useCaseSubmit();
  const { active: persona } = usePersona();
  const stageClosing = useStageClosing();

  const timeline = c.timeline;
  const today = todayIso();
  const rows = stagesOnTimeline(timeline, getStages(), today);
  const overdue = rows.find(({ status }) => status === "overdue");
  const open = rows.find(
    ({ status }) => status === "open" || status === "overdue",
  );
  const stageName = (stage: Stage) => tRoot(`home.stages.${Stage[stage]}`);
  // a settlement ended the case in it, if one did
  const settledIn = settledStageOf(timeline, getStages());
  const judgment = judgmentOf(c);
  const assessmentOutstanding =
    judgment !== undefined &&
    Object.values(judgment.recoveries).some(
      ({ assessment }) => assessment === undefined,
    );

  const save = (changed: Timeline) =>
    Promise.resolve(
      submit({ timeline: JSON.stringify(changed) }, { method: "put" }),
    );
  const setDate = (field: DateField, value: string) => {
    const { [field]: _, ...rest } = timeline ?? {};
    return save(value === "" ? rest : { ...rest, [field]: value });
  };

  // a case cannot start after a stage of it ended
  const firstEnd = getStages()
    .flatMap((stage) => timeline?.stages?.[stage]?.ended ?? [])
    .sort()[0];

  const dateFields: DateField[] =
    persona === "insurer" ? ["start", "lossDate", "notifiedDate"] : ["start"];

  return (
    <Stack spacing={2.25} sx={{ pb: 4 }}>
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
          >
            {t("intro")}
          </Typography>
        </Box>
      </GuideHeader>

      <Paper variant="outlined" sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}>
        <Marked entry="dates">
          <SectionLabel component="h2">{t("dates")}</SectionLabel>
        </Marked>
        <Stack
          direction="row"
          useFlexGap
          sx={{ flexWrap: "wrap", gap: 2, mt: 1.75 }}
        >
          {dateFields.map((field) => (
            <DateEntry
              // what is saved replaces what was typed
              key={`${field}:${timeline?.[field] ?? ""}`}
              label={t(`date.${field}`)}
              value={timeline?.[field] ?? ""}
              max={field === "start" ? firstEnd : undefined}
              disabled={readOnly}
              onCommit={(value) => setDate(field, value)}
              width={200}
            />
          ))}
        </Stack>
      </Paper>

      {overdue?.expectedEnd && (
        <Alert
          severity="warning"
          action={
            !readOnly && (
              <Button
                sx={{ textTransform: "none", fontWeight: 600 }}
                color="inherit"
                variant="outlined"
                onClick={stageClosing.requestClose}
              >
                {t("close", { stage: stageName(overdue.stage) })}
              </Button>
            )
          }
        >
          {t("overdue", {
            stage: stageName(overdue.stage),
            date: showDate(overdue.expectedEnd),
          })}
        </Alert>
      )}

      <StagesChart
        // a stage passed over takes no time
        rows={rows.filter(({ stage }) => !isPassedOver(timeline, stage))}
        today={today}
        stageName={stageName}
      />

      <Paper variant="outlined" sx={{ ...card, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <SectionLabel component="h2">{t("stages")}</SectionLabel>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ "& td, & th": { px: 2.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("column.stage")}</TableCell>
                <TableCell>
                  <Marked entry="duration">{t("column.duration")}</Marked>
                </TableCell>
                <TableCell>{t("column.expectedEnd")}</TableCell>
                <TableCell>
                  <Marked entry="ended">{t("column.ended")}</Marked>
                </TableCell>
                <TableCell>
                  <Marked entry="status">{t("column.status")}</Marked>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                // not reached, nor planned
                const passedOver = isPassedOver(timeline, row.stage);
                return (
                  <TableRow
                    key={row.stage}
                    sx={
                      passedOver
                        ? { "& td, & th": { color: "text.disabled" } }
                        : undefined
                    }
                  >
                    <TableCell component="th" scope="row">
                      {stageName(row.stage)}
                    </TableCell>
                    <TableCell>
                      {passedOver ? (
                        "–"
                      ) : (
                        <MonthsField
                          // a new field once the stored duration changes
                          key={row.months ?? ""}
                          months={row.months}
                          label={t("durationOf", {
                            stage: stageName(row.stage),
                          })}
                          disabled={readOnly}
                          onChange={(months) =>
                            save(withMonths(timeline, row.stage, months))
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.expectedEnd && !passedOver
                        ? showDate(row.expectedEnd)
                        : "–"}
                    </TableCell>
                    <TableCell>
                      {row.ended ? (
                        showDate(row.ended)
                      ) : passedOver ? (
                        "–"
                      ) : row.status === "closed" && !readOnly ? (
                        // closed before the timeline: the user enters the date
                        <DateEntry
                          ariaLabel={t("enterDateOf", {
                            stage: stageName(row.stage),
                          })}
                          value=""
                          {...endDateRange(
                            timeline,
                            getStages(),
                            row.stage,
                            today,
                          )}
                          onCommit={(value) =>
                            value !== "" &&
                            save(withEnded(timeline, row.stage, value))
                          }
                          width={170}
                        />
                      ) : (
                        "–"
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        row={row}
                        passedOver={passedOver}
                        settled={
                          row.stage === settledIn ||
                          !!timeline?.stages?.[row.stage]?.settled
                        }
                      />
                      {row.stage === Stage.Trial && assessmentOutstanding && (
                        <Typography
                          variant="caption"
                          component="p"
                          sx={{ color: "warning.dark", fontWeight: 600 }}
                        >
                          {t("assessmentOutstanding")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.stage === Stage.Trial && judgment && !readOnly && (
                        <Button
                          sx={{ textTransform: "none", fontWeight: 600 }}
                          size="small"
                          variant="outlined"
                          onClick={stageClosing.requestJudgment}
                        >
                          {t("editJudgment")}
                        </Button>
                      )}
                      {row === open && !readOnly && (
                        <Button
                          sx={{ textTransform: "none", fontWeight: 600 }}
                          size="small"
                          variant="outlined"
                          onClick={stageClosing.requestClose}
                        >
                          {t("closeStage")}
                        </Button>
                      )}
                      {row.stage === stageClosing.lastClosed && !readOnly && (
                        <Button
                          sx={{ textTransform: "none", fontWeight: 600 }}
                          size="small"
                          onClick={stageClosing.requestReopen}
                        >
                          {t("reopenStage")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography
          variant="caption"
          component="p"
          sx={{ px: 2.5, py: 1.5, color: "text.secondary" }}
        >
          {t("explanation")}
        </Typography>
      </Paper>

      {stageClosing.dialogs}
    </Stack>
  );
};

/**
 * A date, saved once the field is left or Enter is pressed: browsers change
 * the value of a date field as its calendar is paged and as a date is typed,
 * long before the user has decided on one. Outside `min` and `max` it is not
 * saved, and says why.
 */
const DateEntry = ({
  label,
  ariaLabel,
  value: saved,
  min,
  max,
  disabled,
  onCommit,
  width,
}: {
  label?: string;
  ariaLabel?: string;
  value: IsoDate;
  min?: IsoDate;
  max?: IsoDate;
  disabled?: boolean;
  onCommit: (value: IsoDate) => void;
  width: number;
}) => {
  const [value, setValue] = useState(saved);
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const tooEarly = value !== "" && min !== undefined && value < min;
  const tooLate = max !== undefined && value > max;
  const invalid = tooEarly || tooLate;
  const commit = () => {
    if (!invalid && value !== saved) onCommit(value);
  };
  return (
    <TextField
      type="date"
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      error={invalid}
      helperText={
        tooEarly
          ? t("notBefore", { date: showDate(min) })
          : tooLate
            ? t("notAfter", { date: showDate(max) })
            : undefined
      }
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
      slotProps={{
        ...(label && { inputLabel: { shrink: true } }),
        htmlInput: { min, max, "aria-label": ariaLabel },
      }}
      sx={{ width }}
    />
  );
};

/** A stage's duration in whole months, saved once the field is left. */
const MonthsField = ({
  months,
  label,
  disabled,
  onChange,
}: {
  months?: number;
  label: string;
  disabled: boolean;
  onChange: (months: number | undefined) => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const [text, setText] = useState(months === undefined ? "" : `${months}`);
  const value = text.trim() === "" ? undefined : Number(text);
  const invalid = value !== undefined && !isMonths(value);
  return (
    <TextField
      size="small"
      value={text}
      disabled={disabled}
      error={invalid}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        if (!invalid && value !== months) onChange(value);
      }}
      slotProps={{
        htmlInput: { inputMode: "numeric", "aria-label": label },
        input: {
          endAdornment: (
            <InputAdornment position="end">{t("months")}</InputAdornment>
          ),
        },
      }}
      sx={{ width: 150 }}
    />
  );
};

const StatusBadge = ({
  row,
  passedOver,
  settled,
}: {
  row: StageOnTimeline;
  passedOver: boolean;
  // the case settled in it, or before it
  settled: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const { theme } = useTheme();
  const tones: { [status in StageStatus]?: typeof theme.palette.primary } = {
    closed: theme.palette.primary,
    open: theme.palette.info,
    overdue: theme.palette.warning,
  };
  const tone = tones[row.status];
  const label = settled
    ? t(passedOver ? "notReached" : "status.settled")
    : passedOver
      ? t("passedOver")
      : row.status === "closed" && row.ended === undefined
        ? t("status.closedUndated")
        : row.status === "overdue" && row.expectedEnd
          ? t("status.overdueSince", { date: showDate(row.expectedEnd) })
          : t(`status.${row.status}`);
  return tone && !passedOver ? (
    <TonalBadge tone={tone}>{label}</TonalBadge>
  ) : (
    <Typography
      variant="body2"
      sx={{ color: passedOver ? "text.disabled" : "text.secondary" }}
    >
      {label}
    </Typography>
  );
};

const DAY = 24 * 60 * 60 * 1000;
const time = (date: IsoDate) => new Date(`${date}T00:00:00Z`).getTime();

/**
 * The stages over time, against today: what each took, what each is expected
 * to take, and how long an open stage has run past its expected end. Only
 * stages with dates are drawn; without a start, nothing is.
 */
const StagesChart = ({
  rows,
  today,
  stageName,
}: {
  rows: StageOnTimeline[];
  today: IsoDate;
  stageName: (stage: Stage) => string;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const { theme } = useTheme();
  const drawn = rows.filter(
    (row) => row.begins !== undefined && (row.ended ?? row.expectedEnd),
  );
  if (drawn.length === 0) return null;
  // every date of the rows: those of a case from before dates had to be in
  // order can lie before its first stage
  const times = drawn.flatMap((row) => [
    time(row.begins!),
    time((row.ended ?? row.expectedEnd)!),
  ]);
  const from = Math.min(...times);
  const to = Math.max(time(today), ...times);
  const span = Math.max(to - from, DAY);
  const at = (date: IsoDate) => `${((time(date) - from) / span) * 100}%`;
  const width = (a: IsoDate, b: IsoDate) =>
    `${(Math.max(time(b) - time(a), 0) / span) * 100}%`;
  const colors = {
    closed: theme.palette.primary.main,
    open: theme.palette.grey[500],
    overdue: theme.palette.warning.main,
  };

  return (
    <Paper variant="outlined" sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}>
      <SectionLabel component="h2">{t("chart.title")}</SectionLabel>
      <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr", mt: 1.75 }}>
        {drawn.map((row) => {
          const end = (row.ended ?? row.expectedEnd)!;
          const planned =
            !row.ended && row.status !== "open" && row.status !== "overdue";
          return [
            <Typography
              key={`${row.stage}-name`}
              variant="body2"
              noWrap
              sx={{ py: 0.5, pr: 1 }}
            >
              {stageName(row.stage)}
            </Typography>,
            <Box
              key={`${row.stage}-bar`}
              sx={{ position: "relative", height: 28 }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  height: 16,
                  left: at(row.begins!),
                  width: width(row.begins!, end),
                  borderRadius: 0.75,
                  boxSizing: "border-box",
                  ...(planned
                    ? {
                        border: 1.5,
                        borderStyle: "dashed",
                        borderColor: "grey.500",
                      }
                    : { bgcolor: row.ended ? colors.closed : colors.open }),
                }}
              />
              {row.status === "overdue" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    height: 16,
                    left: at(end),
                    width: width(end, today),
                    bgcolor: colors.overdue,
                    borderRadius: "0 6px 6px 0",
                  }}
                />
              )}
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: at(today),
                  width: 2,
                  bgcolor: "error.main",
                }}
              />
            </Box>,
          ];
        })}
      </Box>
      <Stack
        direction="row"
        useFlexGap
        sx={{ flexWrap: "wrap", gap: 2, mt: 1, color: "text.secondary" }}
      >
        <Typography variant="caption">{t("chart.legend")}</Typography>
      </Stack>
    </Paper>
  );
};
